import { UiSchema } from '@rjsf/utils'

import { TOOLTIP_FOR_TWL_SIXTEEN_FIELD } from '../general'
import {
    fascia1DimensionsFieldKeys,
    fascia2DimensionsFieldKeys,
    frieze1DimensionsFieldKeys,
    frieze2DimensionsFieldKeys,
    soffitDimensionsFieldKeys,
} from './constants'
import { generateDimensionsFields } from '../../../services/form-schema/eave-gable-length/helpers'

export const eaveGableLengthUiSchema = (type: 'Eave' | 'Gable'): UiSchema => ({
    label: {
        'ui:title': 'Label',
        'ui:widget': 'hidden',
    },
    name: {
        'ui:readonly': true,
        'ui:widget': 'hidden',
    },
    folder: {
        'ui:readonly': true,
        'ui:widget': 'hidden',
    },
    selection: {
        'ui:placeholder': 'Select Roofing',
    },

    // sub fascia section
    include_sub_fascia: {
        'ui:title': 'Sub Fascia',
        'ui:field': 'SectionDivider',
        'ui:options': {
            topDividerFieldType: 'switch',
            isLabelBold: true,
        },
    },
    sub_fascia_dimensions: {
        'ui:field': 'LumberDimensionsField',
        'ui:options': {
            tooltips: {
                length: {
                    text: TOOLTIP_FOR_TWL_SIXTEEN_FIELD,
                },
            },
        },
    },
    // end of sub fascia section

    // rafter tail fields section
    robust_rafter_tail: {
        'ui:field': 'SectionDivider',
        'ui:options': {
            topDividerFieldType: 'switch',
            isLabelBold: true,
        },
    },
    rafter_tail_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    rafter_tail_spacing: {
        'ui:title': 'Spacing',
        'ui:placeholder': 'Select Spacing',
    },
    replace_roof_sheathing: {
        'ui:title': 'Replace Roof Sheathing?',
        'ui:field': 'SwitchField',
    },
    // end of rafter tail fields section

    // width fields section
    width: {
        'ui:title': `${type} Width (In)`,
        'ui:placeholder': `Select ${type} Width (In)`,
        'ui:field': 'SectionDivider',
        'ui:options': {
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
        },
    },
    width_other: {
        'ui:placeholder': `Other ${type} Width (In)`,
    },
    // end of width fields section

    // soffit fields section
    soffit_type: {
        'ui:title': 'Type',
        'ui:placeholder': 'Select Soffit Type',
        'ui:field': 'SectionDivider',
        'ui:options': {
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
            label: 'Soffit',
        },
    },
    exposed_soffit: {
        'ui:field': 'SwitchField',
    },
    ...generateDimensionsFields(soffitDimensionsFieldKeys),
    non_vented_soffit: {
        'ui:title': `Vented ${type} Soffit Panel`,
        'ui:field': 'SwitchField',
    },
    soffit_vent_type: {
        'ui:placeholder': 'Select Soffit Vent Type',
    },
    soffit_texture: {
        'ui:title': 'Soffit Texture',
        'ui:placeholder': 'None',
    },
    panel_type: {
        'ui:title': 'Panel Type',
        'ui:placeholder': 'None',
    },
    texture: {
        'ui:title': 'Panel Texture',
        'ui:placeholder': 'None',
    },
    plywood_profile: {
        'ui:title': 'Plywood Profile',
    },
    // end of soffit fields section

    // fascia fields section
    fascia_type: {
        'ui:placeholder': 'Select Fascia Type 1',
        'ui:field': 'SectionDivider',
        'ui:options': {
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
            label: 'Fascia',
        },
    },
    ...generateDimensionsFields(fascia1DimensionsFieldKeys),
    fascia_texture: {
        'ui:placeholder': 'None',
    },
    // end of fascia fields section

    // fascia 2 fields section
    fascia_type_2: {
        'ui:placeholder': 'Select Fascia Type 2',
        'ui:field': 'SectionDivider',
        'ui:options': {
            dropdownWithPlaceholder: true,
            middleDivider: true,
        },
    },
    ...generateDimensionsFields(fascia2DimensionsFieldKeys),
    fascia_texture_2: {
        'ui:placeholder': 'None',
    },
    // end of fascia 2 fields section

    // frieze fields section
    frieze_type: {
        'ui:placeholder': 'Select Frieze Type 1',
        'ui:field': 'SectionDivider',
        'ui:options': {
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
            label: 'Frieze',
        },
    },
    ...generateDimensionsFields(frieze1DimensionsFieldKeys),
    frieze_texture: {
        'ui:placeholder': 'None',
    },
    // end of frieze fields section

    // frieze 2 fields section
    frieze_type_2: {
        'ui:placeholder': 'Select Frieze Type 2',
        'ui:field': 'SectionDivider',
        'ui:options': {
            dropdownWithPlaceholder: true,
            middleDivider: true,
        },
    },
    ...generateDimensionsFields(frieze2DimensionsFieldKeys),
    frieze_texture_2: {
        'ui:placeholder': 'None',
    },
    // end of frieze 2 fields section

    cornice_crown_molding: {
        'ui:title': 'Has Cornice Crown Molding?',
        'ui:field': 'SectionDivider',
        'ui:options': {
            topDividerFieldType: 'switch',
        },
    },

    // pre ROv2 fields
    // when we remove ROv2 flag, ensure we delete this fields from order
    soffit_tg_width: {
        'ui:placeholder': 'Select Soffit TG Width',
    },
    rafter_tail_size: {
        'ui:placeholder': 'Select Rafter Tail Size',
    },
    rafter_tail_length: {
        'ui:placeholder': 'Select Rafter Tail Length',
    },
    fascia_thickness_1: {
        'ui:placeholder': 'Select Fascia Thickness 1',
    },
    fascia_thickness_2: {
        'ui:placeholder': 'Select Fascia Thickness 2',
    },
    fascia_width_1: {
        'ui:placeholder': 'None',
    },
    fascia_width_2: {
        'ui:placeholder': 'None',
    },
    frieze_thickness_1: {
        'ui:placeholder': 'Select Frieze Thickness 1',
    },
    frieze_thickness_2: {
        'ui:placeholder': 'Select Frieze Thickness 2',
    },
    frieze_width_1: {
        'ui:placeholder': 'None',
    },
    frieze_width_2: {
        'ui:placeholder': 'None',
    },
})

export const eaveGableLengthUIOrder = [
    'width',
    'width_other',

    'robust_rafter_tail',
    'rafter_tail_dimensions',
    'rafter_tail_size',
    'rafter_tail_length',
    'rafter_tail_spacing',

    'soffit_type',
    ...soffitDimensionsFieldKeys,
    'exposed_soffit',
    'replace_roof_sheathing',

    'panel_type',
    'texture',
    'plywood_profile',
    'soffit_vent_type',
    'soffit_tg_width',
    'soffit_texture',
    'vented_gable',
    'vented_eave',

    'fascia_type',
    ...fascia1DimensionsFieldKeys,
    'fascia_texture',
    'fascia_thickness_1',
    'fascia_width_1',

    'fascia_type_2',
    ...fascia2DimensionsFieldKeys,
    'fascia_texture_2',
    'fascia_thickness_2',
    'fascia_width_2',

    'frieze_type',
    ...frieze1DimensionsFieldKeys,
    'frieze_texture',
    'frieze_thickness_1',
    'frieze_width_1',

    'frieze_type_2',
    ...frieze2DimensionsFieldKeys,
    'frieze_texture_2',
    'frieze_thickness_2',
    'frieze_width_2',

    'pitch',
    'pitch_other',

    'cornice_crown_molding',
]
